import { useEffect } from 'react';
import { detectPerfTier } from '../utils/perfTier';
import { useNetworkStatus } from './useNetworkStatus';
import { useBatteryStatus } from './useBatteryStatus';

/**
 * Hook que mantém `data-perf-tier` no <html> atualizado em runtime
 * combinando três sinais:
 *
 *  1. Hardware (memory + cores) — fixo, lido uma vez
 *  2. Rede (2G / saveData / offline) — atualiza com Network Information API
 *  3. Bateria — atualiza quando battery level/charging muda
 *
 * Regra: se QUALQUER um dos três sinaliza "modo leve", entra em low tier.
 * Hardware = applyPerfTier (já roda no main.tsx no boot).
 *
 * Lê o tier de hardware do data-attribute (setado por applyPerfTier no boot),
 * combina com network/battery e re-aplica no <html>. Os componentes que
 * dependem de `data-perf-tier` pegam via CSS ou via getAttribute em runtime.
 *
 * Também expõe `data-perf-net-low` e `data-perf-batt-low` no <html> pra
 * que CSS/componentes possam reagir só ao sinal específico, se quiser.
 */
export function useDynamicPerfTier(): void {
  const { isLowBandwidth, online } = useNetworkStatus();
  const { isLowBattery } = useBatteryStatus();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    const hwTier = detectPerfTier();
    const effective = !online || isLowBandwidth || isLowBattery || hwTier === 'low' ? 'low' : 'high';

    root.setAttribute('data-perf-tier', effective);
    root.toggleAttribute('data-perf-net-low', isLowBandwidth || !online);
    root.toggleAttribute('data-perf-batt-low', isLowBattery);
  }, [isLowBandwidth, online, isLowBattery]);
}
